import { Bell, Inbox } from 'lucide-react';
import { useState } from 'react';
import { openAssistant } from '@/assistant/store';
import { useCan } from '@/lib/can';
import { t, tp } from '@/lib/i18n';
import { useInbox } from '@/lib/inbox';
import { Button, EmptyState, ErrorState, IconButton, LoadingState, Popover, useShellLayout } from '@/ui';
import { AppLink, InboxList } from './InboxList';
import s from './TopBar.module.css';

const SHOWN = 6;

/**
 * What waits for this person's decision (the attention inbox, in brief): tasks and approvals
 * assigned to me, my roles or anyone who may do them, and the assistant's proposals — each
 * acted on right here. Home lists everything, data-quality findings and failures included.
 */
export function InboxBell() {
  const inbox = useInbox({ bell: true });
  const canAsk = useCan('core.ai.use');
  const [open, setOpen] = useState(false);
  const layout = useShellLayout();
  if (inbox.parts.length === 0) return null;
  const count = inbox.items.length;
  const close = () => setOpen(false);
  const firstError = inbox.errors[0];
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      width="md"
      label={t('Inbox')}
      trigger={
        <IconButton
          icon={Bell}
          label={count ? tp(count, '{count} item waiting for you', '{count} items waiting for you') : t('Inbox')}
          badge={count || undefined}
        />
      }
    >
      <div className={s.inbox}>
        <div className={s.inboxHead}>
          <span className={s.inboxTitle}>{t('Waiting for you')}</span>
          <span className={s.inboxCount}>{count}</span>
        </div>
        <div className={s.inboxBody}>
          {inbox.isPending ? (
            <LoadingState />
          ) : count === 0 && firstError ? (
            <ErrorState compact error={firstError.error} onRetry={firstError.refetch} />
          ) : count === 0 ? (
            <EmptyState
              compact
              icon={Inbox}
              title={t('Nothing waiting for you')}
              description={t('Tasks, approvals and the assistant’s proposals for you appear here until they are done.')}
            />
          ) : (
            <>
              <InboxList items={inbox.items.slice(0, SHOWN)} compact onNavigate={close} />
              {count > SHOWN && <p className={s.inboxMore}>{t('{count} more on Home', { count: count - SHOWN })}</p>}
              {firstError && <p className={s.inboxMore}>{t('Some items could not be loaded.')}</p>}
            </>
          )}
        </div>
        <div className={s.inboxFoot}>
          <AppLink href="/" className={s.inboxLink} onNavigate={close}>
            {t('Everything on Home')}
          </AppLink>
          {canAsk && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                close();
                openAssistant({ ultrawide: layout === 'ultrawide' });
              }}
            >
              {t('Open the assistant')}
            </Button>
          )}
        </div>
      </div>
    </Popover>
  );
}
